import type { ReactNode } from 'react';
import { styled } from '@mui/material/styles';

import { CALLOUT_W, SIDEBAR_GROUND, SIDEBAR_SHADOW_W, sidebarShadow } from './Frame';

/**
 * The right sidebar's place from 900px up, in either view: the view's own
 * panel, or the callout in its stead.
 *
 * One column rather than two elements swapped: it eases to the width of
 * whatever it holds, out from nothing, from the panel's width to the callout's
 * and back, while the two cross-fade. Each face is pinned to the column's left
 * edge, so as the column opens it slides in from the screen's edge, and as it
 * closes slides back out. The list beside it narrows and widens with it.
 *
 * The panel stays drawn under the callout, faded and inert, so closing the
 * callout shows it again at once, where it was.
 */

const MS = 320;
const EASE_OUT = 'cubic-bezier(0.22, 1, 0.36, 1)';
/** The panel's width: `RightSidebar`'s own clamp. */
const PANEL_W = 'clamp(300px, 20vw, 380px)';

const Column = styled('div')({
  position: 'relative',
  flexShrink: 0,
  alignSelf: 'stretch',
  background: SIDEBAR_GROUND,
  transition: `width ${MS}ms ${EASE_OUT}`,
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  // The shadow it casts on the page to its left, outside the clip below. Over
  // the list's results bar (7), which would otherwise cut it off at the top.
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: '100%',
    width: SIDEBAR_SHADOW_W,
    background: sidebarShadow('left'),
    pointerEvents: 'none',
    zIndex: 7,
    transition: `opacity ${MS}ms ease`,
  },
  '&[data-open="false"]::before': { opacity: 0 },
});

/** Clips each face to the column's width as it eases, leaving the shadow outside it. */
const Clip = styled('div')({
  position: 'absolute',
  inset: 0,
  overflow: 'hidden',
});

const Face = styled('div')({
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  display: 'flex',
  transition: `opacity ${MS - 80}ms ease`,
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
});

interface Props {
  /** What it shows: `rightSidebarHolds`'s answer. */
  holds: 'callout' | 'panel' | null;
  /** The view's own panel, whenever it is open, callout or not. */
  panel: ReactNode | null;
  /** The callout, while it is drawn as the right sidebar at all. */
  callout: ReactNode | null;
}

export default function RightColumn({ holds, panel, callout }: Props) {
  const width = holds === 'callout' ? CALLOUT_W : holds === 'panel' ? PANEL_W : 0;
  const face = (shown: boolean, w: string | number, children: ReactNode) => (
    <Face
      style={{ width: w, opacity: shown ? 1 : 0, pointerEvents: shown ? undefined : 'none' }}
      inert={!shown}
      aria-hidden={!shown || undefined}
    >
      {children}
    </Face>
  );

  return (
    <Column style={{ width }} data-right-column data-open={holds != null}>
      <Clip>
        {panel && face(holds === 'panel', PANEL_W, panel)}
        {callout && face(holds === 'callout', CALLOUT_W, callout)}
      </Clip>
    </Column>
  );
}
